// app/javascript/builder/index.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import CanvasEditor from './CanvasEditor'
import ReviewScreen from './ReviewScreen'
import SummaryScreen from './SummaryScreen'
import CustomerSignInGate from './CustomerSignInGate'
import SaveConflictNotice from './SaveConflictNotice'
import GuidedWelcome from './GuidedWelcome'
import GuidedArtworkSetup from './GuidedArtworkSetup'
import GuidedFileDropOverlay from './GuidedFileDropOverlay'
import GuidedScreenTransition from './GuidedScreenTransition'
import { GUIDED_BUILD_TIMEOUT_MS } from './loadArtworkImage'
import {
  claimBuilderDraft,
  createActiveDraft,
  completeCustomerSignInHandoff,
  deleteUpload,
  fetchBackgroundPreview,
  fetchBuilderDraft,
  fetchCustomerSessionStatus,
  importVendorArtwork,
  normalizeUpload,
  startNewBuilderDraft,
  updateUploadBackground,
} from './api'
import { deriveGuidedStage, GUIDED_STAGES } from './guidedWorkflow'
import { hasPlacedArtwork, imagesArePending } from './draftState'
import useBuilderAutosave, {
  clearAcknowledgedEmergencySnapshot,
  removeEmergencySnapshot,
  readEmergencySnapshot,
} from './useBuilderAutosave'
import { subscribeToCustomerAuthHandoff } from './customerAuthHandoff'
import {
  clearPendingCustomerAuth,
  readPendingCustomerAuth,
  writePendingCustomerAuth,
  writeRequiredCustomerAuth,
} from './customerAuthPending'
import { MAX_BUILDER_IMAGES, useBuilderUploads } from './useBuilderUploads'
import { useShopDesigns } from './useShopDesigns'
import { isFileDrag, useDragDepth } from './useDragDepth'
import {
  deriveDefaultArtworkDimensions,
  expandArtworkSources,
  normalizeArtworkConfig,
} from './artworkSource'
import './guided.css'

const SIGNED_IN_NOTICE_DURATION_MS = 8_000

const container = document.getElementById('builder-root')
const MISSING_ARTWORK_MESSAGE = 'Some artwork files are no longer available. Reupload the missing artwork to continue editing.'

const defaultRedirectTo = (url) => {
  window.location.href = url
}

function defaultSheetSizeId(sheetSizes) {
  return [...sheetSizes].sort((a, b) => (a.width_in * a.height_in) - (b.width_in * b.height_in))[0]?.id ?? null
}

function artworkConfigFallback(image, config) {
  const defaultDimensions = deriveDefaultArtworkDimensions({ ...image, ...(config || {}) })
  return defaultDimensions ? { ...image, ...defaultDimensions } : image
}

function configsFromImages(images, configs = []) {
  const configById = new Map((configs || []).map(config => [String(config.id), config]))

  return Object.fromEntries((images || []).map(image => {
    const config = configById.get(String(image.id))
    return [image.id, normalizeArtworkConfig(config, artworkConfigFallback(image, config))]
  }))
}

function applyConfigsToImages(images, imageConfigs) {
  return (images || []).map(image => {
    const config = imageConfigs[image.id]
    return {
      ...image,
      ...normalizeArtworkConfig(config, artworkConfigFallback(image, config)),
    }
  })
}

function restoredImagesFromResume(data) {
  const imageConfigs = data.image_configs || []
  const configById = new Map(imageConfigs.map(config => [String(config.id), config]))
  const uploadedImages = Array.isArray(data.uploaded_images) ? data.uploaded_images : []

  return uploadedImages
    .filter(image => image.storage_available !== false)
    .map(image => normalizeUpload({
      ...image,
      ...(configById.get(String(image.id)) || {}),
    }))
}

function referencedImageIds(layout) {
  return new Set((layout?.sheets || []).flatMap(sheet =>
    (sheet.items || [])
      .map(item => item.uploaded_image_id)
      .filter(id => id !== undefined && id !== null)
      .map(Number)
      .filter(id => Number.isFinite(id) && id > 0)
  ))
}

function missingReferencedImageIds(layout, images) {
  const availableIds = new Set((images || []).map(image => Number(image.id)))
  return [...referencedImageIds(layout)].filter(id => !availableIds.has(id))
}

function pruneLayoutToImages(layout, images) {
  if (!layout) return layout
  const imageIds = new Set((images || []).map(image => Number(image.id)))

  return {
    ...layout,
    sheets: (layout.sheets || []).map(sheet => ({
      ...sheet,
      items: (sheet.items || []).filter(item => imageIds.has(Number(item.uploaded_image_id))),
    })),
  }
}

function clientQuoteFromPayload(quote) {
  if (!quote) return null

  return {
    hasContent: true,
    widthInches: quote.width_inches,
    heightInches: quote.height_inches,
    totalHeightInches: quote.height_inches,
    sheetCount: quote.sheet_count,
    sheetHeightsInches: quote.sheet_heights_inches,
    squareFeet: quote.square_feet,
    rateCents: quote.price_per_square_foot_cents,
    totalCents: quote.total_cents,
    exceedsMaximum: false,
    billingUnit: quote.billing_unit,
    tiersVersion: quote.tiers_version ?? null,
    sheets: quote.sheets ? quote.sheets.map(s => ({ sheet: s.sheet, heightIn: s.height_in, priceCents: s.price_cents })) : null,
  }
}

function hasCompleteReviewPreview(layout, thumbnails) {
  const sheetCount = layout?.sheets?.length || 0
  return sheetCount > 0 &&
    thumbnails.length >= sheetCount &&
    thumbnails.slice(0, sheetCount).every(Boolean)
}

export function Builder({
  vendorToken,
  builderSessionId,
  sheetSizes,
  pricingSettings,
  editRef,
  resumeToken,
  draftPublicId,
  initialCustomerAuthenticated = false,
  initialCustomerEmail = null,
  signedIn = false,
  guidedBuilderEnabled = false,
  vendorName = null,
  artworkRightsPolicyVersion = null,
  redirectTo = defaultRedirectTo,
}) {
  const customerPortalPath = `/builder/${vendorToken}/customer`
  const recoveryPath = editRef
    ? `/builder/${vendorToken}/gangsheets/${editRef}`
    : resumeToken
      ? `/builder/${vendorToken}/resume/${resumeToken}`
      : null

  const [screen, setScreen]                 = useState('loading')
  const [images, setImages]                 = useState([])
  const [imageConfigs, setImageConfigs]     = useState({})
  const [selectedSheetSizeId, setSelectedSheetSizeId] = useState(() => defaultSheetSizeId(sheetSizes))
  const [sheetSize, setSheetSize]           = useState(null)
  const [layout, setLayout]                 = useState(null)
  const [thumbnails, setThumbnails]         = useState([])
  const [quantity, setQuantity]             = useState(1)
  const [artworkQualityAcknowledged, setArtworkQualityAcknowledged] = useState(false)
  const [loadError, setLoadError]           = useState(null)
  const [resumeCheckoutUrl, setResumeCheckoutUrl] = useState(null)
  const [activePricingSettings, setActivePricingSettings] = useState(pricingSettings)
  const [liveQuote, setLiveQuote]             = useState(null)
  const [builderDraft, setBuilderDraft]        = useState(null)
  const [canvasRestoreVersion, setCanvasRestoreVersion] = useState(0)
  const [saveConflictResolving, setSaveConflictResolving] = useState(false)
  const [customerAuthenticated, setCustomerAuthenticated] = useState(initialCustomerAuthenticated)
  const [currentCustomerEmail, setCurrentCustomerEmail] = useState(initialCustomerEmail)
  const [signInGateOpen, setSignInGateOpen] = useState(false)
  const [signedInNotice, setSignedInNotice] = useState(signedIn)
  const [manualDraftPublicId, setManualDraftPublicId] = useState(null)
  const [workspaceDraftPublicId, setWorkspaceDraftPublicId] = useState(null)
  const [guidedBuildRequestId, setGuidedBuildRequestId] = useState(null)
  const [guidedBuildError, setGuidedBuildError] = useState(null)
  const activeGuidedBuildRef = useRef(null)
  activeGuidedBuildRef.current = guidedBuildRequestId
  const [reviewOrigin, setReviewOrigin] = useState(null)
  const [setupDraftPublicId, setSetupDraftPublicId] = useState(null)
  const [reviewPendingAuth, setReviewPendingAuth] = useState(false)
  const [reviewPreviewRequestId, setReviewPreviewRequestId] = useState(null)
  const [customerSignInRequest, setCustomerSignInRequest] = useState(null)
  const [transitioning, setTransitioning] = useState(false)
  const initializationStartedRef = useRef(false)
  const skipInitialAutosaveRef = useRef(true)
  const emergencyRecoveryRef = useRef(null)
  const adoptionInFlightRef = useRef(false)
  // handleBackgroundApply is defined above the autosave hook, so it reaches
  // flush through a ref rather than the binding it cannot see yet.
  const flushAutosaveRef = useRef(null)
  // Same ordering problem: the autosave hook is constructed before
  // adoptDraftGeometry exists, so the conflict callback reaches it via a ref.
  const adoptDraftGeometryRef = useRef(null)
  const backgroundProcessingIdsRef = useRef(null)
  const guidedBuildSequenceRef = useRef(0)
  const reviewPreviewSequenceRef = useRef(0)
  const customerAuthRefreshInFlightRef = useRef(false)
  const customerAuthStatusInFlightRef = useRef(false)
  const signedInReturnHandledRef = useRef(false)
  const customerSignInPending = customerSignInRequest?.phase === 'pending'
  const customerSignInHandoffToken = customerSignInRequest?.handoffToken || null
  const uploadScopeKey = builderDraft?.public_id || (
    editRef ? `edit:${editRef}` : resumeToken ? `resume:${resumeToken}` : null
  )
  const uploads = useBuilderUploads({
    vendorToken,
    builderSessionId,
    builderDraftPublicId: builderDraft?.public_id,
    uploadScopeKey,
    images,
    onImagesChange: setImages,
  })
  const { dragActive, rootDragHandlers, reset: resetDrag } = useDragDepth()

  const shopDesigns = useShopDesigns({
    enabled: Boolean(guidedBuilderEnabled && !recoveryPath && builderDraft?.public_id),
    vendorToken,
    builderSessionId,
    builderDraftPublicId: builderDraft?.public_id,
  })
  const shopDesignsSearch = useMemo(() => ({
    searchable: shopDesigns.searchable,
    query: shopDesigns.query,
    activeQuery: shopDesigns.activeQuery,
    onQueryChange: shopDesigns.setQuery,
    hasMore: shopDesigns.hasMore,
    loadingMore: shopDesigns.loadingMore,
    onLoadMore: shopDesigns.loadMore,
    total: shopDesigns.total,
    categories: shopDesigns.categories,
    category: shopDesigns.category,
    onCategoryChange: shopDesigns.setCategory,
    categoryNotice: shopDesigns.categoryNotice,
  }), [
    shopDesigns.searchable, shopDesigns.query, shopDesigns.activeQuery, shopDesigns.setQuery,
    shopDesigns.hasMore, shopDesigns.loadingMore, shopDesigns.loadMore, shopDesigns.total,
    shopDesigns.categories, shopDesigns.category, shopDesigns.setCategory, shopDesigns.categoryNotice,
  ])

  const manualModeRequested = Boolean(
    builderDraft?.public_id && manualDraftPublicId === builderDraft.public_id
  )
  const workspaceEntered = Boolean(
    builderDraft?.public_id && workspaceDraftPublicId === builderDraft.public_id
  )
  const setupRequested = Boolean(
    builderDraft?.public_id && setupDraftPublicId === builderDraft.public_id
  )
  const guidedStage = deriveGuidedStage({
    guidedBuilderEnabled,
    legacyEditReference: editRef,
    legacyResumeToken: resumeToken,
    manualModeRequested,
    workspaceEntered,
    uploadedImages: images,
    uploadsActive: uploads.uploadsActive,
    layoutJson: layout,
    currentScreen: screen,
  })
  const guidedDragEnabled = Boolean(
    guidedBuilderEnabled &&
      !recoveryPath &&
      builderDraft &&
      screen === 'canvas' &&
      guidedBuildRequestId === null &&
      reviewPreviewRequestId === null
  )

  useEffect(() => {
    if (
      screen !== 'review' ||
      !builderDraft ||
      !customerAuthenticated ||
      recoveryPath ||
      // Capturing mid-adoption would bake the geometry being replaced into the
      // review preview. The effect re-runs once transitioning clears.
      transitioning ||
      reviewPreviewRequestId !== null ||
      hasCompleteReviewPreview(layout, thumbnails)
    ) {
      return
    }

    // Never leave an authenticated customer on a metadata-only Review card.
    // Restore the existing Fabric layout and recapture its preview without
    // changing placement or running Auto Arrange again.
    reviewPreviewSequenceRef.current += 1
    setScreen('canvas')
    setReviewPreviewRequestId(reviewPreviewSequenceRef.current)
  }, [
    builderDraft,
    customerAuthenticated,
    layout,
    recoveryPath,
    reviewPreviewRequestId,
    screen,
    thumbnails,
    transitioning,
  ])

  useEffect(() => {
    if (!signedInNotice) return undefined

    const timeoutId = window.setTimeout(() => setSignedInNotice(false), SIGNED_IN_NOTICE_DURATION_MS)
    return () => window.clearTimeout(timeoutId)
  }, [signedInNotice])

  useEffect(() => {
    const currentDraftPublicId = builderDraft?.public_id
    if (!currentDraftPublicId || recoveryPath) return

    if (customerAuthenticated) {
      clearPendingCustomerAuth(vendorToken, currentDraftPublicId)
      return
    }

    const pendingRequest = readPendingCustomerAuth(vendorToken, currentDraftPublicId)
    if (!pendingRequest) return

    setCustomerSignInRequest(pendingRequest)
    setReviewOrigin(pendingRequest.reviewOrigin)
    setReviewPendingAuth(true)
    setSignInGateOpen(true)
  }, [builderDraft?.public_id, customerAuthenticated, recoveryPath, vendorToken])

  const selectedSheetSize = useMemo(
    () => sheetSizes.find(size => Number(size.id) === Number(selectedSheetSizeId)),
    [selectedSheetSizeId, sheetSizes]
  )
  const baseSheetSize = useMemo(
    () => selectedSheetSize || sheetSize,
    [selectedSheetSize, sheetSize]
  )
  const isDynamicPricing = activePricingSettings?.pricing_mode === 'per_square_foot'
  const editorSheetSize = useMemo(() => {
    if (!baseSheetSize) return null
    if (!isDynamicPricing) return baseSheetSize

    return {
      ...baseSheetSize,
      label: 'Automatic-height sheet',
      width_in: activePricingSettings.width_inches,
      height_in: activePricingSettings.min_height_inches,
    }
  }, [
    baseSheetSize,
    isDynamicPricing,
    activePricingSettings?.width_inches,
    activePricingSettings?.min_height_inches,
  ])
  const arrangementSheetSize = useMemo(() => (
    isDynamicPricing && editorSheetSize
      ? { ...editorSheetSize, height_in: activePricingSettings.max_height_inches }
      : editorSheetSize
  ), [activePricingSettings?.max_height_inches, editorSheetSize, isDynamicPricing])
  const quotedSheetSize = useMemo(() => {
    if (!isDynamicPricing || !liveQuote?.heightInches) return editorSheetSize

    return {
      ...editorSheetSize,
      height_in: liveQuote.sheetHeightsInches?.[0] || liveQuote.heightInches,
    }
  }, [
    editorSheetSize,
    isDynamicPricing,
    liveQuote?.heightInches,
    liveQuote?.sheetHeightsInches,
  ])
  const configuredImages = useMemo(() => applyConfigsToImages(images, imageConfigs), [images, imageConfigs])
  const arrangementImages = useMemo(
    () => expandArtworkSources(images, imageConfigs),
    [images, imageConfigs]
  )
  const draftImageConfigs = useMemo(() => Object.entries(imageConfigs).map(([id, config]) => ({
    id: Number(id),
    ...config,
  })), [imageConfigs])
  const handlePricingChange = useCallback((quote) => {
    setLiveQuote(quote)
  }, [])
  const handleGeometryConflict = useCallback(() => {
    adoptDraftGeometryRef.current?.()
  }, [])
  const handleDraftMetadataChange = useCallback((metadata) => {
    setBuilderDraft(previous => previous ? { ...previous, ...metadata } : previous)
  }, [])
  const handleBackgroundPreview = useCallback((id, mode) => (
    fetchBackgroundPreview(
      vendorToken,
      builderSessionId,
      id,
      mode,
      builderDraft?.public_id
    )
  ), [builderDraft?.public_id, builderSessionId, vendorToken])
  const handleBackgroundApply = useCallback(async (id, mode) => {
    // Flush first, then freeze. Without this the customer's last edits are still
    // sitting in the autosave snapshot when the server starts rejecting geometry
    // writes, and adoption would discard them.
    await flushAutosaveRef.current?.()
    const update = await updateUploadBackground(
      vendorToken,
      builderSessionId,
      id,
      mode,
      builderDraft?.public_id
    )
    setImages(previousImages => previousImages.map(image => {
      if (String(image.id) !== String(id)) return image

      const backgroundChanged = Number(update.backgroundRevision) !== Number(image.backgroundRevision ?? 0)
      return {
        ...image,
        ...update,
        ...(backgroundChanged ? { imageUrl: null, previewUrl: null } : {}),
      }
    }))
    setThumbnails([])
    return update
  }, [builderDraft?.public_id, builderSessionId, vendorToken])

  useEffect(() => {
    if (images.length === 0) return

    setImageConfigs(previousConfigs => {
      let changed = false
      const nextConfigs = { ...previousConfigs }

      images.forEach(image => {
        const previous = previousConfigs[image.id]
        const normalized = normalizeArtworkConfig(previous, artworkConfigFallback(image, previous))
        if (
          !previous ||
          previous.printWidthIn !== normalized.printWidthIn ||
          previous.printHeightIn !== normalized.printHeightIn ||
          previous.quantity !== normalized.quantity ||
          previous.included !== normalized.included
        ) {
          nextConfigs[image.id] = normalized
          changed = true
        }
      })

      return changed ? nextConfigs : previousConfigs
    })
  }, [images])

  useEffect(() => {
    const currentDraftPublicId = builderDraft?.public_id
    const enteringWorkspace = guidedStage === GUIDED_STAGES.SETUP ||
      guidedStage === GUIDED_STAGES.REVIEW

    if (
      screen === 'canvas' &&
      currentDraftPublicId &&
      workspaceDraftPublicId !== currentDraftPublicId &&
      enteringWorkspace
    ) {
      setWorkspaceDraftPublicId(currentDraftPublicId)
    }
  }, [builderDraft?.public_id, guidedStage, screen, workspaceDraftPublicId])

  useEffect(() => {
    if (!guidedDragEnabled) {
      resetDrag()
      return undefined
    }
    if (!dragActive) return undefined

    const handleCancellation = () => resetDrag()
    const handleKeyDown = event => {
      if (event.key === 'Escape') resetDrag()
    }

    window.addEventListener('dragend', handleCancellation)
    window.addEventListener('blur', handleCancellation)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('dragend', handleCancellation)
      window.removeEventListener('blur', handleCancellation)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [dragActive, guidedDragEnabled, resetDrag])
  const {
    status: saveStatus,
    lastSavedAt,
    queueSave,
    recoverSnapshot,
    flush: flushAutosave,
    rebase: rebaseAutosave,
    discard: discardAutosave,
  } = useBuilderAutosave({
    vendorToken,
    builderSessionId,
    draft: builderDraft,
    onDraftMetadataChange: handleDraftMetadataChange,
    onGeometryConflict: handleGeometryConflict,
  })
  flushAutosaveRef.current = flushAutosave

  const buildDraftSnapshot = useCallback((layoutOverride) => ({
    vendor_sheet_size_id: selectedSheetSizeId,
    layout_json: layoutOverride || layout || {},
    image_configs: draftImageConfigs,
    uploaded_image_ids: images.map(image => image.id),
    quantity,
    pricing_settings: activePricingSettings,
  }), [activePricingSettings, draftImageConfigs, images, layout, quantity, selectedSheetSizeId])

  // A removal rewrites the artwork and, with auto-trim on, the geometry that
  // describes it. The browser cannot patch its way there: CanvasEditor's restore
  // is mount-only, and the autosave snapshot is a whole layout, so replaying it
  // would overwrite the server's retarget. Adoption replaces parent state
  // wholesale and lets the canvas remount on the new epoch.
  const adoptDraftGeometry = useCallback(async (savedDraft = null) => {
    const publicId = builderDraft?.public_id
    if (!publicId || recoveryPath || adoptionInFlightRef.current) return false

    adoptionInFlightRef.current = true
    setTransitioning(true)
    try {
      const data = savedDraft || await fetchBuilderDraft(vendorToken, builderSessionId, publicId)
      const adoptedImages = restoredImagesFromResume(data)

      // Discard, never rebase: pending local work describes artwork that no
      // longer exists at those coordinates.
      if (!await discardAutosave?.(data)) return false
      emergencyRecoveryRef.current = null

      setImages(adoptedImages)
      setImageConfigs(configsFromImages(adoptedImages, data.image_configs))
      setLayout(pruneLayoutToImages(data.layout_json, adoptedImages))
      setThumbnails([])
      setSheetSize(data.sheet_size)
      setSelectedSheetSizeId(data.sheet_size?.id ?? null)
      setQuantity(data.quantity || 1)
      setLiveQuote(clientQuoteFromPayload(data.quote))
      setActivePricingSettings(data.pricing_settings || pricingSettings)
      setBuilderDraft(data)
      if (savedDraft) setCanvasRestoreVersion(previous => previous + 1)
      skipInitialAutosaveRef.current = true
      setLoadError(null)
      return true
    } catch {
      setLoadError('Could not load the updated artwork. Refresh to continue editing.')
      return false
    } finally {
      adoptionInFlightRef.current = false
      setTransitioning(false)
    }
  }, [
    builderDraft?.public_id,
    builderSessionId,
    discardAutosave,
    pricingSettings,
    recoveryPath,
    vendorToken,
  ])
  adoptDraftGeometryRef.current = adoptDraftGeometry

  const resolveSaveConflict = useCallback(async (choice) => {
    setSaveConflictResolving(true)
    try {
      const publicId = builderDraft?.public_id
      const data = await fetchBuilderDraft(vendorToken, builderSessionId, publicId)
      if (data.public_id !== publicId || data.status !== 'active') {
        throw new Error('This sheet is no longer editable. Keep this tab open and contact the shop.')
      }
      if (choice === 'local') {
        if (Number(data.geometry_epoch ?? 0) !== Number(builderDraft.geometry_epoch ?? 0)) {
          throw new Error('The artwork has changed since this tab was opened. To avoid applying old sizes to changed artwork, go back and choose Load saved version.')
        }
        if (!await rebaseAutosave(data, { keepLocalVersion: true })) {
          throw new Error('Could not save this version. Your arrangement is still open. Close other editing tabs and try again, or load the saved version.')
        }
      } else if (choice === 'saved') {
        if (!await adoptDraftGeometry(data)) throw new Error('Could not load the saved version. Keep this tab open and try again.')
        setArtworkQualityAcknowledged(false)
        setScreen('canvas')
      }
    } finally {
      setSaveConflictResolving(false)
    }
  }, [adoptDraftGeometry, builderDraft, builderSessionId, rebaseAutosave, vendorToken])

  // A removal finishing is the moment the server may have retargeted. Watch the
  // transition out of "processing" rather than the epoch, which only arrives
  // with a fetched draft.
  useEffect(() => {
    const processingNow = new Set(
      (images || [])
        .filter(image => image.backgroundProcessingStatus === 'processing')
        .map(image => image.id)
    )
    const previous = backgroundProcessingIdsRef.current
    backgroundProcessingIdsRef.current = processingNow
    if (previous === null) return
    // A customer resolving an ordinary conflict must explicitly choose before
    // a background update replaces the arrangement behind the dialog.
    if (saveStatus === 'conflict' || saveConflictResolving) return
    if ([ ...previous ].some(id => !processingNow.has(id))) adoptDraftGeometry()
  }, [images, adoptDraftGeometry, saveStatus, saveConflictResolving])

  // Restore from edit/resume link — fetch gangsheet, bypass builder session (session-free)
  useEffect(() => {
    if (initializationStartedRef.current) return
    initializationStartedRef.current = true

    const request = recoveryPath
      ? fetch(recoveryPath).then(r => r.ok ? r.json() : Promise.reject(r))
      : draftPublicId
        ? fetchBuilderDraft(vendorToken, builderSessionId, draftPublicId)
        : createActiveDraft(vendorToken, builderSessionId, defaultSheetSizeId(sheetSizes))

    request
      .then(data => {
        const restoredImages = restoredImagesFromResume(data)
        const serverRevision = Number(data.client_revision || 0)
        const localSnapshot = recoveryPath
          ? null
          : readEmergencySnapshot(vendorToken, data.public_id)
        const shouldRecoverLocal = localSnapshot?.client_revision > serverRevision
        if (localSnapshot && !shouldRecoverLocal) {
          clearAcknowledgedEmergencySnapshot(vendorToken, data.public_id, serverRevision)
        }
        const recoveredSheetSize = shouldRecoverLocal
          ? sheetSizes.find(size => Number(size.id) === Number(localSnapshot.vendor_sheet_size_id))
          : null
        const sourceLayout = shouldRecoverLocal ? localSnapshot.layout_json : data.layout_json
        const missingImageIds = missingReferencedImageIds(sourceLayout, restoredImages)
        const restorableLayout = pruneLayoutToImages(sourceLayout, restoredImages)

        setSheetSize(recoveredSheetSize || data.sheet_size)
        setSelectedSheetSizeId(
          recoveredSheetSize?.id ?? data.sheet_size?.id ?? null
        )
        setLayout(restorableLayout)
        setImages(restoredImages)
        setImageConfigs(configsFromImages(
          restoredImages,
          shouldRecoverLocal ? localSnapshot.image_configs : data.image_configs
        ))
        setResumeCheckoutUrl(data.checkout_url || null)
        setActivePricingSettings(
          (shouldRecoverLocal && localSnapshot.pricing_settings) ||
          data.pricing_settings ||
          pricingSettings
        )
        setLiveQuote(clientQuoteFromPayload(data.quote))
        if (!recoveryPath) setBuilderDraft(data)
        setQuantity(shouldRecoverLocal ? localSnapshot.quantity : (data.quantity || 1))
        setLoadError(missingImageIds.length > 0 ? MISSING_ARTWORK_MESSAGE : null)
        emergencyRecoveryRef.current = shouldRecoverLocal ? localSnapshot : null
        skipInitialAutosaveRef.current = true
        setScreen(resumeToken ? 'summary' : 'canvas')
      })
      .catch(() => {
        setLoadError('Could not load your design. Please start over.')
        setScreen('canvas')
      })
  }, [])

  useEffect(() => {
    if (!builderDraft || recoveryPath) return
    if (emergencyRecoveryRef.current) {
      const localSnapshot = emergencyRecoveryRef.current
      emergencyRecoveryRef.current = null
      skipInitialAutosaveRef.current = false
      recoverSnapshot(localSnapshot)
      return
    }
    if (skipInitialAutosaveRef.current) {
      skipInitialAutosaveRef.current = false
      return
    }

    queueSave(buildDraftSnapshot())
  }, [
    builderDraft?.public_id,
    buildDraftSnapshot,
    imageConfigs,
    images,
    layout,
    quantity,
    queueSave,
    recoverSnapshot,
    recoveryPath,
    selectedSheetSizeId,
  ])

  const handleLayoutChange = useCallback((serializedLayout) => {
    setLayout(serializedLayout)
  }, [])

  const enterManualEditor = useCallback(() => {
    if (!builderDraft?.public_id) return

    setGuidedBuildRequestId(null)
    setGuidedBuildError(null)
    setSetupDraftPublicId(null)
    setManualDraftPublicId(builderDraft.public_id)
    setScreen('canvas')
  }, [builderDraft?.public_id])

  const requireCustomerAuthentication = useCallback((origin) => {
    const request = writeRequiredCustomerAuth(
      vendorToken,
      builderDraft?.public_id,
      origin
    ) || {
      phase: 'required',
      email: '',
      reviewOrigin: origin,
      handoffToken: null,
      requestedAt: null,
      expiresAt: null,
    }
    setCustomerSignInRequest(request)
    setReviewOrigin(origin)
    setReviewPendingAuth(true)
    setSignInGateOpen(true)
  }, [builderDraft?.public_id, vendorToken])

  const handleCanvasContinue = useCallback(async (serializedLayout, captureThumbnails, origin, isCurrent = () => true) => {
    if (!isCurrent()) return false
    setLayout(serializedLayout)
    setThumbnails(captureThumbnails)
    if (builderDraft) {
      const canContinue = await flushAutosave(buildDraftSnapshot(serializedLayout))
      if (!canContinue) return false
    }
    if (!isCurrent()) return false
    // Submitted gangsheet recovery links predate BuilderDrafts and may lead
    // directly back to an existing Shopify checkout. They do not create a new
    // Gangsheet, so preserve that established recovery path.
    if (recoveryPath && !builderDraft) {
      setScreen('review')
      return true
    }
    if (customerAuthenticated) {
      if (builderDraft) {
        try {
          // Claim this exact draft only after the customer's Review/Build action,
          // never because another account's sign-in link opened in this browser.
          const claimed = await claimBuilderDraft(vendorToken, builderSessionId, builderDraft.public_id)
          if (!isCurrent()) return false
          if (Number(claimed.geometry_epoch ?? 0) !== Number(builderDraft.geometry_epoch ?? 0)) {
            await adoptDraftGeometry(claimed)
            return false
          }
          if (!await rebaseAutosave(claimed)) return false
        } catch {
          setLoadError('Could not save this gang sheet to your account. Your layout is still here. Try Review design again.')
          return false
        }
      }
      if (!isCurrent()) return false
      setScreen('review')
    } else requireCustomerAuthentication(origin)
    return true
  }, [
    adoptDraftGeometry,
    builderDraft,
    builderSessionId,
    buildDraftSnapshot,
    customerAuthenticated,
    flushAutosave,
    recoveryPath,
    rebaseAutosave,
    requireCustomerAuthentication,
    vendorToken,
  ])

  const handleManualContinue = useCallback((serializedLayout, captureThumbnails) => {
    return handleCanvasContinue(serializedLayout, captureThumbnails, 'manual')
  }, [handleCanvasContinue])

  const handleGuidedBuildError = useCallback(message => {
    activeGuidedBuildRef.current = null
    setGuidedBuildError(message || 'Could not build this gang sheet. Please try again.')
    setGuidedBuildRequestId(null)
    setReviewPendingAuth(false)
    if (builderDraft?.public_id) {
      clearPendingCustomerAuth(vendorToken, builderDraft.public_id)
    }
    setCustomerSignInRequest(null)
    setSignInGateOpen(false)
    setReviewOrigin(null)
  }, [builderDraft?.public_id, vendorToken])

  useEffect(() => {
    if (guidedBuildRequestId === null) return
    const timer = window.setTimeout(() => {
      if (activeGuidedBuildRef.current !== guidedBuildRequestId) return
      handleGuidedBuildError('This build took too long. Your artwork and settings are still here. Check your connection and retry build.')
    }, GUIDED_BUILD_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [guidedBuildRequestId, handleGuidedBuildError])

  useEffect(() => () => { activeGuidedBuildRef.current = null }, [])

  const handleGuidedContinue = useCallback(async (serializedLayout, captureThumbnails) => {
    const requestId = guidedBuildRequestId
    const isCurrent = () => requestId !== null && activeGuidedBuildRef.current === requestId
    try {
      const accepted = await handleCanvasContinue(serializedLayout, captureThumbnails, 'guided', isCurrent)
      if (!isCurrent()) return false
      if (!accepted) {
        handleGuidedBuildError('We could not save the generated layout. Check your connection and try again.')
        return false
      }

      activeGuidedBuildRef.current = null
      setGuidedBuildRequestId(null)
      return true
    } catch {
      if (!isCurrent()) return false
      handleGuidedBuildError('We could not save the generated layout. Check your connection and try again.')
      return false
    }
  }, [guidedBuildRequestId, handleCanvasContinue, handleGuidedBuildError])

  const handleCustomerAuthenticated = useCallback(async (session) => {
    if (!session?.authenticated || customerAuthRefreshInFlightRef.current) return false

    customerAuthRefreshInFlightRef.current = true
    try {
      await flushAutosave(buildDraftSnapshot())
      const refreshedDraft = await fetchBuilderDraft(
        vendorToken,
        builderSessionId,
        builderDraft?.public_id
      )
      const rebased = await rebaseAutosave(refreshedDraft)
      if (!rebased) throw new Error('Could not synchronize the signed-in draft')
      const saved = await flushAutosave(buildDraftSnapshot())
      if (!saved) throw new Error('Could not save the synchronized draft')

      setCustomerAuthenticated(true)
      setCurrentCustomerEmail(session.email || null)
      if (builderDraft?.public_id) {
        clearPendingCustomerAuth(vendorToken, builderDraft.public_id)
      }
      setCustomerSignInRequest(null)
      setSignInGateOpen(false)
      setSignedInNotice(true)
      setLoadError(null)
      if (reviewPendingAuth) {
        setReviewPendingAuth(false)
        if (reviewOrigin === 'manual_entry') {
          setReviewOrigin(null)
          enterManualEditor()
        } else if (hasCompleteReviewPreview(layout, thumbnails)) {
          setScreen('review')
        } else {
          reviewPreviewSequenceRef.current += 1
          setReviewPreviewRequestId(reviewPreviewSequenceRef.current)
        }
      }
      return true
    } catch {
      setLoadError('You are signed in, but this tab could not refresh your saved gang sheet. Refresh the page to continue safely.')
      return false
    } finally {
      customerAuthRefreshInFlightRef.current = false
    }
  }, [
    builderDraft?.public_id,
    builderSessionId,
    buildDraftSnapshot,
    enterManualEditor,
    flushAutosave,
    layout,
    rebaseAutosave,
    reviewOrigin,
    reviewPendingAuth,
    thumbnails,
    vendorToken,
  ])

  const handleReviewPreviewCaptured = useCallback((capturedLayout, capturedThumbnails) => {
    setLayout(capturedLayout)
    setThumbnails(capturedThumbnails)
    setReviewPreviewRequestId(null)
    setScreen('review')
  }, [])

  const handleReviewPreviewError = useCallback((message) => {
    setReviewPreviewRequestId(null)
    setLoadError(message || 'Could not prepare the design preview. Return to the layout and try again.')
    setScreen('canvas')
  }, [])

  const checkPendingCustomerAuthentication = useCallback(async () => {
    if (customerAuthStatusInFlightRef.current) return

    customerAuthStatusInFlightRef.current = true
    try {
      const session = await fetchCustomerSessionStatus(vendorToken)
      const completedSession = !session.authenticated && customerSignInHandoffToken && builderDraft?.public_id
        ? await completeCustomerSignInHandoff(
            vendorToken,
            builderSessionId,
            builderDraft.public_id,
            customerSignInHandoffToken
          )
        : session
      if (completedSession.authenticated) await handleCustomerAuthenticated(completedSession)
    } catch {
      // The handoff page remains available as a safe fallback when this check fails.
    } finally {
      customerAuthStatusInFlightRef.current = false
    }
  }, [
    builderDraft?.public_id,
    builderSessionId,
    customerSignInHandoffToken,
    handleCustomerAuthenticated,
    vendorToken,
  ])

  useEffect(() => {
    if (!customerSignInPending || customerAuthenticated || !builderDraft?.public_id) return undefined

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkPendingCustomerAuthentication()
    }
    const unsubscribe = subscribeToCustomerAuthHandoff(
      vendorToken,
      builderDraft.public_id,
      checkPendingCustomerAuthentication
    )
    checkPendingCustomerAuthentication()
    const intervalId = window.setInterval(checkPendingCustomerAuthentication, 5_000)
    window.addEventListener('focus', checkPendingCustomerAuthentication)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      unsubscribe()
      window.clearInterval(intervalId)
      window.removeEventListener('focus', checkPendingCustomerAuthentication)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    builderDraft?.public_id,
    checkPendingCustomerAuthentication,
    customerAuthenticated,
    customerSignInPending,
    vendorToken,
  ])

  const clearCustomerSignInRequest = useCallback(() => {
    if (builderDraft?.public_id) {
      clearPendingCustomerAuth(vendorToken, builderDraft.public_id)
    }
    setCustomerSignInRequest(null)
  }, [builderDraft?.public_id, vendorToken])

  const resetCustomerSignInRequest = useCallback(() => {
    requireCustomerAuthentication(reviewOrigin)
  }, [requireCustomerAuthentication, reviewOrigin])

  const cancelSignInGate = useCallback(() => {
    clearCustomerSignInRequest()
    setSignInGateOpen(false)
    setReviewPendingAuth(false)
    setGuidedBuildRequestId(null)
    setGuidedBuildError(null)
    if (
      (reviewOrigin === 'guided' || reviewOrigin === 'manual_entry') &&
      builderDraft?.public_id
    ) {
      setManualDraftPublicId(null)
      setSetupDraftPublicId(builderDraft.public_id)
    }
    setReviewOrigin(null)
    setScreen('canvas')
  }, [builderDraft?.public_id, clearCustomerSignInRequest, reviewOrigin])

  const handleOpenManualEditor = useCallback(() => {
    if (!builderDraft?.public_id || transitioning) return

    if (customerAuthenticated || recoveryPath) {
      enterManualEditor()
      return
    }

    requireCustomerAuthentication('manual_entry')
  }, [
    builderDraft?.public_id,
    customerAuthenticated,
    enterManualEditor,
    recoveryPath,
    requireCustomerAuthentication,
    transitioning,
  ])

  const handleImageConfigChange = useCallback((id, config) => {
    const sourceImage = images.find(image => String(image.id) === String(id))
    setImageConfigs(previous => ({
      ...previous,
      [id]: normalizeArtworkConfig({ ...(previous[id] || {}), ...config }, sourceImage),
    }))
  }, [images])

  const handleDeleteArtworkSource = useCallback(async id => {
    if (builderDraft?.public_id) {
      await deleteUpload(vendorToken, builderSessionId, id, builderDraft.public_id)
    } else {
      await deleteUpload(vendorToken, builderSessionId, id)
    }

    const remainingImages = images.filter(image => String(image.id) !== String(id))
    setImages(previous => previous.filter(image => String(image.id) !== String(id)))
    setImageConfigs(previous => {
      const next = { ...previous }
      delete next[id]
      return next
    })
    setLayout(previous => pruneLayoutToImages(previous, remainingImages))
    setThumbnails([])
    setResumeCheckoutUrl(null)
  }, [builderDraft?.public_id, builderSessionId, images, vendorToken])

  const handleRetryArtwork = useCallback(image => {
    const remainingImages = images.filter(candidate => String(candidate.id) !== String(image.id))
    setImages(previous => previous.filter(candidate => String(candidate.id) !== String(image.id)))
    setImageConfigs(previous => {
      const next = { ...previous }
      delete next[image.id]
      return next
    })
    setLayout(previous => pruneLayoutToImages(previous, remainingImages))
    setThumbnails([])
  }, [images])

  const handleImportVendorArtwork = useCallback(async publicId => {
    if (!builderDraft?.public_id) throw new Error('Could not add the shop design')

    const imported = await importVendorArtwork(
      vendorToken,
      builderSessionId,
      publicId,
      builderDraft.public_id
    )
    setImages(previous => previous.some(image => String(image.id) === String(imported.id))
      ? previous
      : [...previous, imported])
    setThumbnails([])
    setResumeCheckoutUrl(null)
    return imported
  }, [builderDraft?.public_id, builderSessionId, vendorToken])

  const handleBuildGangSheet = useCallback(() => {
    // Guided setup mounts no canvas, so the freeze in CanvasEditor cannot reach
    // this path. Arranging against geometry that is being replaced would place
    // artwork at coordinates the server has already moved.
    if (transitioning) return

    setGuidedBuildError(null)
    if (builderDraft?.public_id) setSetupDraftPublicId(builderDraft.public_id)
    guidedBuildSequenceRef.current += 1
    setGuidedBuildRequestId(guidedBuildSequenceRef.current)
  }, [builderDraft?.public_id, transitioning])

  useEffect(() => {
    if (
      !guidedBuilderEnabled ||
      !signedIn ||
      !draftPublicId ||
      !customerAuthenticated ||
      screen !== 'canvas' ||
      !builderDraft?.public_id ||
      signedInReturnHandledRef.current ||
      transitioning ||
      images.length === 0 ||
      imagesArePending(images) ||
      images.some(image => image.backgroundProcessingStatus === 'processing')
    ) {
      return
    }

    signedInReturnHandledRef.current = true
    setReviewOrigin('guided')

    if (hasPlacedArtwork(layout)) {
      reviewPreviewSequenceRef.current += 1
      setReviewPreviewRequestId(reviewPreviewSequenceRef.current)
    } else {
      handleBuildGangSheet()
    }
  }, [
    builderDraft?.public_id,
    customerAuthenticated,
    draftPublicId,
    guidedBuilderEnabled,
    handleBuildGangSheet,
    images,
    layout,
    screen,
    signedIn,
    transitioning,
  ])

  const handleGuidedFileDrop = useCallback((event) => {
    const files = isFileDrag(event) ? event.dataTransfer?.files : null
    rootDragHandlers.onDrop(event)
    if (files?.length) uploads.uploadFiles(files)
  }, [rootDragHandlers, uploads.uploadFiles])

  const handleCanvasAddArtwork = useCallback((serializedLayout) => {
    setLayout(serializedLayout)
    setThumbnails([])
    setArtworkQualityAcknowledged(false)
  }, [])

  const handleSelectedSheetSizeChange = useCallback((id) => {
    const nextSheetSize = sheetSizes.find(size => Number(size.id) === Number(id))
    setSelectedSheetSizeId(id)
    setSheetSize(nextSheetSize || null)
    setLayout(previous => pruneLayoutToImages(previous, configuredImages))
    setThumbnails([])
    setResumeCheckoutUrl(null)
  }, [configuredImages, sheetSizes])

  const handleReviewContinue = useCallback(async (qualityAcknowledged = false) => {
    if (builderDraft) {
      const canContinue = await flushAutosave(buildDraftSnapshot())
      if (!canContinue) return
    }
    setArtworkQualityAcknowledged(qualityAcknowledged)
    if (resumeCheckoutUrl) redirectTo(resumeCheckoutUrl)
    else setScreen('summary')
  }, [builderDraft, buildDraftSnapshot, flushAutosave, redirectTo, resumeCheckoutUrl])

  const handleStartNew = useCallback(async () => {
    const currentDraftPublicId = builderDraft?.public_id
    if (!currentDraftPublicId) throw new Error('Could not identify the current draft')

    await startNewBuilderDraft(
      vendorToken,
      builderSessionId,
      currentDraftPublicId,
      selectedSheetSizeId
    )
    if (guidedBuilderEnabled) {
      setManualDraftPublicId(null)
      setWorkspaceDraftPublicId(null)
      setGuidedBuildRequestId(null)
      setGuidedBuildError(null)
      setReviewOrigin(null)
      setSetupDraftPublicId(null)
      setReviewPendingAuth(false)
      setReviewPreviewRequestId(null)
      guidedBuildSequenceRef.current = 0
      reviewPreviewSequenceRef.current = 0
    }
    clearPendingCustomerAuth(vendorToken, currentDraftPublicId)
    setCustomerSignInRequest(null)
    setSignInGateOpen(false)
    const removeAbandonedSnapshot = () => {
      removeEmergencySnapshot(vendorToken, currentDraftPublicId)
    }
    removeAbandonedSnapshot()
    window.addEventListener('pagehide', removeAbandonedSnapshot, { once: true })
    redirectTo(`/builder/${vendorToken}`)
  }, [
    builderDraft?.public_id,
    builderSessionId,
    guidedBuilderEnabled,
    redirectTo,
    selectedSheetSizeId,
    vendorToken,
  ])

  if (screen === 'loading') {
    return <div className="builder-loading">Loading your design…</div>
  }

  if (screen === 'canvas') {
    if (!recoveryPath && !builderDraft) {
      return <div className="builder-loading">{loadError || 'Could not initialize autosave.'}</div>
    }
    if (!editorSheetSize) {
      return <div className="builder-loading">No sheet sizes are configured for this builder.</div>
    }

    const showGuidedWelcome = guidedStage === GUIDED_STAGES.WELCOME ||
      guidedStage === GUIDED_STAGES.PREPARING
    const guidedGateUsesSetupBackdrop = signInGateOpen && (
      customerSignInRequest?.reviewOrigin === 'guided' ||
      customerSignInRequest?.reviewOrigin === 'manual_entry'
    )
    const guidedSetupActive = guidedStage === GUIDED_STAGES.SETUP || (
      setupRequested && guidedStage !== GUIDED_STAGES.MANUAL
    ) || guidedGateUsesSetupBackdrop
    const showGuidedSetup = guidedSetupActive &&
      guidedBuildRequestId === null &&
      reviewPreviewRequestId === null
    const showGuidedBuilding = guidedSetupActive && guidedBuildRequestId !== null
    const canvasContent = (
      <>
        {(saveStatus === 'conflict' || saveConflictResolving) && <SaveConflictNotice onResolve={resolveSaveConflict} />}
        {signedInNotice && (
          <div className="builder-signed-in-notice">
            <span className="builder-signed-in-notice__icon" aria-hidden="true">✓</span>
            <span className="builder-signed-in-notice__message" role="status" aria-live="polite">
              Signed in{currentCustomerEmail ? ` as ${currentCustomerEmail}` : ''}. Your gang sheet is ready.
            </span>
            <button
              type="button"
              aria-label="Dismiss sign-in confirmation"
              onClick={() => setSignedInNotice(false)}
            >
              ×
            </button>
          </div>
        )}
        <GuidedScreenTransition
          screenKey={showGuidedWelcome ? 'welcome' : showGuidedSetup ? 'setup' : 'editor'}
        >
          {showGuidedWelcome ? (
            <GuidedWelcome
              vendorName={vendorName}
              customerPortalPath={customerPortalPath}
              images={images}
              uploads={uploads}
              maxImageCount={MAX_BUILDER_IMAGES}
              onOpenManualEditor={handleOpenManualEditor}
            />
          ) : showGuidedSetup ? (
            <GuidedArtworkSetup
              vendorName={vendorName}
              customerPortalPath={customerPortalPath}
              vendorToken={vendorToken}
              builderSessionId={builderSessionId}
              builderDraftPublicId={builderDraft?.public_id}
              images={images}
              imageConfigs={imageConfigs}
              sheetSizes={sheetSizes}
              selectedSheetSizeId={selectedSheetSizeId}
              onSelectedSheetSizeIdChange={handleSelectedSheetSizeChange}
              pricingSettings={activePricingSettings}
              arrangementSheetSize={arrangementSheetSize}
              uploads={uploads}
              maxImageCount={MAX_BUILDER_IMAGES}
              vendorArtworks={shopDesigns.artworks}
              vendorArtworksLoading={shopDesigns.loading}
              vendorArtworksError={shopDesigns.error}
              shopDesignsSearch={shopDesignsSearch}
              onImportVendorArtwork={handleImportVendorArtwork}
              onImageConfigChange={handleImageConfigChange}
              onRemoveImage={handleDeleteArtworkSource}
              onRetryImage={handleRetryArtwork}
              onBackgroundPreview={handleBackgroundPreview}
              onBackgroundApply={handleBackgroundApply}
              onArrangeManually={handleOpenManualEditor}
              onBuild={handleBuildGangSheet}
              buildError={guidedBuildError}
              onStartNew={!recoveryPath ? handleStartNew : undefined}
              transitioning={transitioning}
            />
          ) : (
            <CanvasEditor
              transitioning={transitioning}
              // CanvasEditor's restore is mount-only, so adoption is a remount:
              // React rebuilds the component and the existing, already-tested
              // mount restore runs against the authoritative draft. Keyed on
              // geometry_epoch and explicit restore, never lock_version — the
              // latter moves on every autosave and would remount on each save.
              key={`geometry-${builderDraft?.geometry_epoch ?? 0}-restore-${canvasRestoreVersion}`}
              images={configuredImages}
              arrangementImages={arrangementImages}
              sheetSize={editorSheetSize}
              vendorToken={vendorToken}
              builderSessionId={builderSessionId}
              customerPortalPath={customerPortalPath}
              sheetSizes={sheetSizes}
              selectedSheetSizeId={editorSheetSize.id}
              onSelectedSheetSizeIdChange={handleSelectedSheetSizeChange}
              imageConfigs={imageConfigs}
              onImagesChange={setImages}
              onImageConfigsChange={setImageConfigs}
              uploads={uploads}
              onContinue={showGuidedBuilding ? handleGuidedContinue : handleManualContinue}
              onAddArtwork={handleCanvasAddArtwork}
              initialLayout={layout}
              loadError={loadError}
              pricingSettings={activePricingSettings}
              onPricingChange={handlePricingChange}
              builderDraftPublicId={builderDraft?.public_id}
              onLayoutChange={handleLayoutChange}
              saveStatus={saveStatus}
              lastAutosavedAt={lastSavedAt}
              onStartNew={!recoveryPath ? handleStartNew : undefined}
              guidedBuildRequestId={showGuidedBuilding ? guidedBuildRequestId : undefined}
              onGuidedBuildError={handleGuidedBuildError}
              reviewPreviewRequestId={reviewPreviewRequestId}
              onReviewPreviewCaptured={handleReviewPreviewCaptured}
              onReviewPreviewError={handleReviewPreviewError}
              onDeleteArtworkSource={handleDeleteArtworkSource}
              onBackgroundPreview={handleBackgroundPreview}
              onBackgroundApply={handleBackgroundApply}
              vendorArtworks={shopDesigns.artworks}
              vendorArtworksLoading={shopDesigns.loading}
              vendorArtworksError={shopDesigns.error}
              shopDesignsSearch={shopDesignsSearch}
              onImportVendorArtwork={handleImportVendorArtwork}
              maxImageCount={MAX_BUILDER_IMAGES}
            />
          )}
        </GuidedScreenTransition>
        {signInGateOpen && (
          <CustomerSignInGate
            vendorToken={vendorToken}
            builderDraftPublicId={builderDraft?.public_id}
            initialEmail={customerSignInRequest?.email || currentCustomerEmail || ''}
            pendingRequest={customerSignInPending ? customerSignInRequest : null}
            cancelLabel={
              reviewOrigin === 'guided' || reviewOrigin === 'manual_entry'
                ? 'Back to artwork setup'
                : 'Back to layout'
            }
            onCancel={cancelSignInGate}
            onUseDifferentEmail={resetCustomerSignInRequest}
            onRequestAccepted={(request) => {
              const pendingRequest = { ...request, phase: 'pending', reviewOrigin }
              setCustomerSignInRequest(pendingRequest)
              writePendingCustomerAuth(
                vendorToken,
                builderDraft?.public_id,
                pendingRequest
              )
            }}
          />
        )}
      </>
    )

    if (!guidedDragEnabled) return canvasContent

    return (
      <div
        data-testid="guided-builder-shell"
        onDragEnterCapture={rootDragHandlers.onDragEnter}
        onDragOverCapture={rootDragHandlers.onDragOver}
        onDragLeaveCapture={rootDragHandlers.onDragLeave}
        onDropCapture={handleGuidedFileDrop}
      >
        {canvasContent}
        {dragActive && <GuidedFileDropOverlay />}
      </div>
    )
  }

  if (screen === 'review') {
    return (
      <>
      {(saveStatus === 'conflict' || saveConflictResolving) && <SaveConflictNotice onResolve={resolveSaveConflict} />}
      <ReviewScreen
        layout={layout}
        thumbnails={thumbnails}
        sheetSize={quotedSheetSize}
        images={configuredImages}
        quantity={quantity}
        pricingMode={activePricingSettings?.pricing_mode}
        quote={liveQuote}
        onQuantityChange={setQuantity}
        onEdit={() => {
          setArtworkQualityAcknowledged(false)
          enterManualEditor()
        }}
        onAddArtwork={() => {
          setArtworkQualityAcknowledged(false)
          setGuidedBuildRequestId(null)
          setGuidedBuildError(null)
          if (guidedBuilderEnabled && !recoveryPath && reviewOrigin === 'guided') {
            if (builderDraft?.public_id) setSetupDraftPublicId(builderDraft.public_id)
          }
          setScreen('canvas')
        }}
        onContinue={handleReviewContinue}
      />
      </>
    )
  }

  if (screen === 'summary') {
    return (
      <>
      {(saveStatus === 'conflict' || saveConflictResolving) && <SaveConflictNotice onResolve={resolveSaveConflict} />}
      <SummaryScreen
        layout={layout}
        thumbnails={thumbnails}
        sheetSize={quotedSheetSize}
        images={configuredImages}
        quantity={quantity}
        artworkQualityAcknowledged={artworkQualityAcknowledged}
        pricingMode={activePricingSettings?.pricing_mode}
        quote={liveQuote}
        vendorToken={vendorToken}
        builderSessionId={builderSessionId}
        onBack={() => setScreen('review')}
        redirectTo={redirectTo}
        customerPortalPath={customerPortalPath}
        builderDraftPublicId={builderDraft?.public_id}
        customerEmail={currentCustomerEmail}
        beforeSubmit={builderDraft ? () => flushAutosave(buildDraftSnapshot()) : null}
        geometryEpoch={Number.isInteger(builderDraft?.geometry_epoch) ? builderDraft.geometry_epoch : null}
        onGeometryConflict={builderDraft ? adoptDraftGeometry : null}
        readOnlyRecovery={Boolean(resumeToken)}
        recoveryCheckoutUrl={resumeCheckoutUrl}
        exactShopifyCheckoutAvailable={Boolean(activePricingSettings?.shopify_exact_checkout_available)}
        checkoutMode={activePricingSettings?.checkout_mode || 'shopify'}
        vendorName={vendorName}
        artworkRightsPolicyVersion={artworkRightsPolicyVersion}
        onPricingSettingsChange={setActivePricingSettings}
      />
      </>
    )
  }
}

if (container) {
  container.classList.remove('builder-loading')

  const vendorToken      = container.dataset.vendorToken
  const builderSessionId = container.dataset.builderSessionId
  const guidedBuilderEnabled = container.dataset.guidedBuilderEnabled === 'true'
  const vendorName       = container.dataset.vendorName || null
  const artworkRightsPolicyVersion = container.dataset.artworkRightsPolicyVersion || null
  const sheetSizes       = JSON.parse(container.dataset.sheetSizes || '[]')
  const pricingSettings  = JSON.parse(container.dataset.pricingSettings || '{}')
  const editRef          = new URLSearchParams(window.location.search).get('edit')
  const resumeToken      = new URLSearchParams(window.location.search).get('resume')
  const draftPublicId    = new URLSearchParams(window.location.search).get('draft')
  const initialCustomerAuthenticated = container.dataset.customerAuthenticated === 'true'
  const initialCustomerEmail = container.dataset.currentCustomerEmail || null
  const signedIn = container.dataset.signedIn === 'true'

  createRoot(container).render(
    <Builder
      vendorToken={vendorToken}
      builderSessionId={builderSessionId}
      guidedBuilderEnabled={guidedBuilderEnabled}
      vendorName={vendorName}
      artworkRightsPolicyVersion={artworkRightsPolicyVersion}
      sheetSizes={sheetSizes}
      pricingSettings={pricingSettings}
      editRef={editRef}
      resumeToken={resumeToken}
      draftPublicId={draftPublicId}
      initialCustomerAuthenticated={initialCustomerAuthenticated}
      initialCustomerEmail={initialCustomerEmail}
      signedIn={signedIn}
    />
  )
}
